import { useCallback, useMemo, useState } from "react";
import { TextField, Button } from "@/components/shared";
import { Checkbox } from "@mui/material";
import isEqual from "lodash-es/isEqual";
import { useDialogPortal } from "@/hooks";
import { DIALOGS } from "@/types/dialog.types";
import { PostHogEvent } from "@/posthog/posthog-capture-names";
import { PosthogHelper } from "@/posthog/posthog-helper";
import type { ExitLockCapturedParams } from "./exit-lock-capture-params.type";
import type { WithdrawLockTransactionProps } from "@/components/transaction";
import type { ExitLockWhaleRetainDialogProps } from "./WhaleRetain/exit-lock-whale-retain-dialog.types";
import { EXIT_LOCK_CONTACT } from "./exit-lock-whale-retain.config";

type ExitFeedbackFormProps = { exitLockCaptureParams: ExitLockCapturedParams };

const defaultForm = {
    isWhaleToRetain: false,
    isMovingFunds: false,
    isChangingLockDuration: false,
    isExitingInfinifi: false,
    otherReason: "",
};

// contact team via discord
const openInfinifiDiscord = () => {
    window.open("https://discord.com/invite/infinifi", "_blank");
};

const openExitLockContact = () => {
    window.open(EXIT_LOCK_CONTACT.href, "_blank");
};

export const ExitLockFeedbackForm = ({ exitLockCaptureParams }: ExitFeedbackFormProps) => {
    const { openDialog } = useDialogPortal();

    const [form, setForm] = useState(defaultForm);
    const {
        isMovingFunds: movingFunds,
        isChangingLockDuration: changingLockDuration,
        isExitingInfinifi: exitingPosition,
        otherReason,
    } = useMemo(() => form, [form]);

    const isFormUpdated = useMemo(() => !isEqual(form, defaultForm), [form]);

    // update form state
    const handleFormUpdate = useCallback(
        (field: keyof typeof defaultForm, value: (typeof defaultForm)[keyof typeof defaultForm]) => {
            setForm((prev) => ({ ...prev, [field]: value }));
        },
        [setForm],
    );

    // handle continue action
    const handleContinue = useCallback(async () => {
        // Submit feedback if the form has been updated from default state
        if (isFormUpdated) {
            try {
                if (!exitLockCaptureParams.userAddress) throw new Error("User address is required to submit feedback.");

                PosthogHelper.capture({
                    event_name: PostHogEvent.ExitLockFeedbackDialog.ContinueButton.clickedAfterAddingFeedback,
                    properties: {
                        form,
                        ...exitLockCaptureParams,
                    },
                    userIdentifier: exitLockCaptureParams.userAddress,
                    allowTemporaryOptIn: true, // allowed for feedback submission
                });
            } catch (error) {
                console.error("Error submitting feedback:", error);
            }
        }

        const continueToExit = () => {
            openDialog({
                dialogType: DIALOGS.WITHDRAW_LOCK_CONFIRM,
                dialogProps: {
                    lockDuration: exitLockCaptureParams.lockDuration,
                    withdrawLockAmount: exitLockCaptureParams.exitSize,
                } satisfies WithdrawLockTransactionProps,
            });
        };

        // If not a whale to retain, skip any whale retention strategy
        const isNotWhale = !exitLockCaptureParams.isWhaleToRetain;
        const isWhaleButNotExiting = exitLockCaptureParams.isWhaleToRetain && !form.isExitingInfinifi;

        if (isNotWhale || isWhaleButNotExiting) {
            continueToExit();
            return;
        }

        // Otherwise attempt to retain the whale via the ExitLockWhaleDialog
        openDialog({
            dialogType: DIALOGS.EXIT_RETAIN_WHALE,
            dialogProps: { exitLockCaptureParams } satisfies ExitLockWhaleRetainDialogProps,
        });
    }, [form, isFormUpdated, exitLockCaptureParams, openDialog]);

    const contactButtonLabel = useMemo(() => {
        if (exitLockCaptureParams.isWhaleToRetain) {
            return `Contact ${EXIT_LOCK_CONTACT.name} (${EXIT_LOCK_CONTACT.title})`;
        }
        return "Contact Team on Discord";
    }, [exitLockCaptureParams.isWhaleToRetain]);

    const handleContactClick = useCallback(() => {
        if (exitLockCaptureParams.isWhaleToRetain) {
            openExitLockContact();
            return;
        }
        openInfinifiDiscord();
    }, [exitLockCaptureParams.isWhaleToRetain]);

    const reasonOptionClassName =
        "flex items-start gap-3 rounded-lg px-4 py-1 text-left text-[20px] leading-9 text-dark transition-colors md:text-[18px] md:leading-8";

    return (
        <form
            onSubmit={(e) => {
                e.preventDefault();
                void handleContinue();
            }}
        >
            <div className="flex flex-col gap-5 text-dark">
                <span className="mt-2 text-[22px] font-bold leading-8 md:text-[20px] md:leading-7">
                    Please let us know why you&apos;re exiting:
                </span>
                <div className="flex flex-col gap-0 select-none" role="group" aria-label="Exit reason">
                    <label className={`${reasonOptionClassName} ${movingFunds ? "text-primary font-medium" : ""}`}>
                        <Checkbox
                            checked={movingFunds}
                            onChange={(e) => handleFormUpdate("isMovingFunds", e.target.checked)}
                            className="!p-0.5"
                        />
                        <span>Moving funds to Liquid Staking (iUSD, siUSD)</span>
                    </label>
                    <label
                        className={`${reasonOptionClassName} ${changingLockDuration ? "text-primary font-medium" : ""}`}
                    >
                        <Checkbox
                            checked={changingLockDuration}
                            onChange={(e) => handleFormUpdate("isChangingLockDuration", e.target.checked)}
                            className="!p-0.5"
                        />
                        <span>Changing Unlock Duration</span>
                    </label>
                    <label className={`${reasonOptionClassName} ${exitingPosition ? "text-primary font-medium" : ""}`}>
                        <Checkbox
                            checked={exitingPosition}
                            onChange={(e) => handleFormUpdate("isExitingInfinifi", e.target.checked)}
                            className="!p-0.5"
                        />
                        <span>Exiting infiniFi</span>
                    </label>
                </div>
                <TextField
                    placeholder="Other (please specify)"
                    multiline
                    rows={4}
                    value={otherReason}
                    onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) =>
                        handleFormUpdate("otherReason", e.target.value)
                    }
                    className="mt-2 rounded-[12px] border-[1px] border-primary/20 px-4 py-3 text-dark text-[16px] leading-6"
                    type="text"
                />
                <div className="mb-4 grid grid-cols-1 gap-4 sm:grid-cols-2 sm:gap-6">
                    <Button
                        type="button"
                        onClick={handleContactClick}
                        variant="secondary"
                        className="h-[52px] border-[1px] border-primary bg-transparent text-primary"
                        fullWidth
                    >
                        <span className="truncate px-3">{contactButtonLabel}</span>
                    </Button>
                    <Button type="submit" className="h-[52px] text-[18px] leading-6" variant="primary" fullWidth>
                        <span className="text-nowrap">Confirm Unlock</span>
                    </Button>
                </div>
            </div>
        </form>
    );
};
